import React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link'; // Use MUI Link
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { styled } from '@mui/material/styles';
import { Helmet } from 'react-helmet';

// --- Placeholders - Replace with your actual details ---
const companyName = "TradesPad";
const websiteUrl = "https://tradespad.com";
const supportEmail = "support@tradespad.com";
const lastUpdatedDate = "May 4, 2025"; // Match ToS update date or use current
const companyAddress = null;
// --- End Placeholders ---

// Optional: Custom styled ListItem for better indentation or style
const StyledListItem = styled(ListItem)(({ theme }) => ({
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    alignItems: 'flex-start', // Align text nicely if it wraps
}));

export default function Privacy() {
    return (
        <Box
            id="privacy-policy"
            sx={{
                width: '100%',
                bgcolor: 'background.paper', // Or use 'white'
                py: { xs: 10, sm: 15 }, // Vertical padding
            }}
        >
            <Helmet>
                <title>TradesPad - Privacy Policy</title>
            </Helmet>
            <Container maxWidth="lg"> {/* Using 'lg' for better text readability */}
                <Typography variant="h3" component="h1" gutterBottom align="center">
                    {companyName} - Privacy Policy
                </Typography>
                <Typography variant="subtitle1" align="center" color="text.secondary" gutterBottom>
                    Last Updated: {lastUpdatedDate}
                </Typography>

                <Typography variant="body1" paragraph sx={{ mt: 4 }}>
                    This privacy notice discloses the privacy practices for {companyName} ("us", "we", or "our") and applies solely to information collected through our website ({websiteUrl}) and associated services (collectively, the "Service").
                </Typography>
                <Typography variant="body1" paragraph>
                    This page informs you of our policies regarding the collection, use, and disclosure of personal data when you use our Service and the choices you have associated with that data.
                </Typography>
                <Typography variant="body1" paragraph>
                    We use your data to provide and improve the Service. By using the Service, you agree to the collection and use of information in accordance with this policy. Unless otherwise defined in this Privacy Policy, terms used here have the same meanings as in our Terms of Service.
                </Typography>
                <Typography variant="body1" paragraph>
                    This privacy notice will notify you of the following:
                </Typography>
                <List dense sx={{ listStyleType: 'decimal', pl: 4, mb: 2 }}>
                    <StyledListItem sx={{ display: 'list-item' }}>
                        <ListItemText primary="What personally identifiable information is collected from you through the Service, how it is used, and with whom it may be shared." />
                    </StyledListItem>
                    <StyledListItem sx={{ display: 'list-item' }}>
                        <ListItemText primary="What choices are available to you regarding the use of your data." />
                    </StyledListItem>
                    <StyledListItem sx={{ display: 'list-item' }}>
                        <ListItemText primary="The security procedures in place to protect your information." />
                    </StyledListItem>
                    <StyledListItem sx={{ display: 'list-item' }}>
                        <ListItemText primary="How you can correct any inaccuracies in the information or request deletion." />
                    </StyledListItem>
                </List>

                {/* Section: Information Collection and Use */}
                <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
                    Information Collection and Use
                </Typography>
                <Typography variant="body1" paragraph>
                    We are the sole owners of the information collected via the Service. We only have access to/collect information that you voluntarily give us via email, through use of the Service (e.g., account registration, entering trade data), or other direct contact from you.
                </Typography>
                <Typography variant="body1" paragraph>
                    We collect several different types of information for various purposes to provide and improve our Service to you:
                </Typography>
                <List dense sx={{ listStyleType: 'disc', pl: 4 }}>
                    <StyledListItem sx={{ display: 'list-item' }}>
                        <ListItemText
                            primary={<strong>Personal Data:</strong>}
                            secondary="While using our Service, we may ask you to provide us with certain personally identifiable information that can be used to contact or identify you ('Personal Data'). This may include, but is not limited to: Email address, First name and last name."
                        />
                    </StyledListItem>
                    <StyledListItem sx={{ display: 'list-item' }}>
                        <ListItemText
                            primary={<strong>Trading Data:</strong>}
                            secondary="Information related to the trades you log using the Service, including details you enter, notes, and any associated images you upload (subject to storage limits outlined in our Terms of Service). This data is linked to your account but treated with strict confidentiality."
                        />
                    </StyledListItem>
                    <StyledListItem sx={{ display: 'list-item' }}>
                        <ListItemText
                            primary={<strong>Usage Data:</strong>}
                            secondary="We may also collect information on how the Service is accessed and used ('Usage Data'). This Usage Data may include information such as your computer's Internet Protocol address (e.g., IP address, which may be anonymized), browser type, browser version, the pages of our Service that you visit, the time and date of your visit, the time spent on those pages, unique device identifiers, and other diagnostic data. We may use third-party Service Providers, such as Google Analytics, to monitor and analyze the use of our Service. This helps us improve the Service."
                        />
                    </StyledListItem>
                    <StyledListItem sx={{ display: 'list-item' }}>
                        <ListItemText
                            primary={<strong>Tracking & Cookies Data:</strong>}
                            secondary="We use cookies and similar tracking technologies to track the activity on our Service and hold certain information. Cookies are files with a small amount of data which may include an anonymous unique identifier. These can be our own cookies or cookies from third-party services, including analytics providers. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our Service."
                        />
                    </StyledListItem>
                    <StyledListItem sx={{ display: 'list-item' }}>
                        <ListItemText
                            primary={<strong>Payment Data:</strong>}
                            secondary="If you subscribe to a paid plan, we require payment information. This is securely processed by our third-party payment processors (e.g., Polar.sh). We do not store your full credit card number on our servers."
                        />
                    </StyledListItem>
                </List>

                {/* Section: Use of Data */}
                <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
                    Use of Data
                </Typography>
                <Typography variant="body1" paragraph>
                    {companyName} uses the collected data for various purposes:
                </Typography>
                <List dense sx={{ listStyleType: 'disc', pl: 4 }}>
                    <StyledListItem sx={{ display: 'list-item' }}>
                        <ListItemText primary="To provide and maintain our Service (e.g., allow you to log and analyze trades)." />
                    </StyledListItem>
                    <StyledListItem sx={{ display: 'list-item' }}>
                        <ListItemText primary="To manage your account and subscription." />
                    </StyledListItem>
                    <StyledListItem sx={{ display: 'list-item' }}>
                        <ListItemText primary="To notify you about changes to our Service or policies." />
                    </StyledListItem>
                    <StyledListItem sx={{ display: 'list-item' }}>
                        <ListItemText primary="To provide customer support." />
                    </StyledListItem>
                    <StyledListItem sx={{ display: 'list-item' }}>
                        <ListItemText primary="To gather analysis or valuable information so that we can improve our Service (using primarily anonymized or aggregated Usage Data)." />
                    </StyledListItem>
                    <StyledListItem sx={{ display: 'list-item' }}>
                        <ListItemText primary="To monitor the usage of our Service and detect/prevent technical issues or fraud." />
                    </StyledListItem>
                    <StyledListItem sx={{ display: 'list-item' }}>
                        <ListItemText primary="To process your payments securely via third-party processors." />
                    </StyledListItem>
                    <StyledListItem sx={{ display: 'list-item' }}>
                        <ListItemText primary="To provide you with news, special offers, and general information about other goods, services, and events which we offer that are similar to those that you have already purchased or enquired about, unless you have opted not to receive such information." />
                    </StyledListItem>
                </List>

                {/* Section: Sharing */}
                <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
                    Sharing Your Information
                </Typography>
                <Typography variant="body1" paragraph>
                    We are committed to maintaining your trust, and we want you to understand when and with whom we may share the information we collect. **We will not sell or rent your Personal Data or individual Trading Data to anyone.**
                </Typography>
                <Typography variant="body1" paragraph>
                    We do not share your Personal Data or Trading Data with third parties except in the following limited circumstances:
                </Typography>
                <List dense sx={{ listStyleType: 'disc', pl: 4 }}>
                    <StyledListItem sx={{ display: 'list-item' }}>
                        <ListItemText primary={<strong>Service Providers:</strong>} secondary="We may employ third-party companies and individuals to facilitate our Service ('Service Providers'), provide the Service on our behalf, perform Service-related services (e.g., payment processing, data storage/hosting, website analytics, customer support), or assist us in analyzing how our Service is used. These third parties have access to your data only to perform these tasks on our behalf and are obligated not to disclose or use it for any other purpose." />
                    </StyledListItem>
                    <StyledListItem sx={{ display: 'list-item' }}>
                        <ListItemText primary={<strong>Legal Requirements:</strong>} secondary={`We may disclose your data in the good faith belief that such action is necessary to: (a) comply with a legal obligation; (b) protect and defend the rights or property of ${companyName}; (c) prevent or investigate possible wrongdoing in connection with the Service; (d) protect the personal safety of users of the Service or the public; or (e) protect against legal liability.`} />
                    </StyledListItem>
                    <StyledListItem sx={{ display: 'list-item' }}>
                        <ListItemText primary={<strong>Business Transfers:</strong>} secondary={`If ${companyName} is involved in a merger, acquisition, or asset sale, your Personal Data may be transferred. We will provide notice before your Personal Data is transferred and becomes subject to a different Privacy Policy.`} />
                    </StyledListItem>
                    <StyledListItem sx={{ display: 'list-item' }}>
                        <ListItemText primary={<strong>Aggregated or Anonymized Data:</strong>} secondary="We may share aggregated or anonymized information (information that does not directly identify you) publicly and with third parties." />
                    </StyledListItem>
                </List>
                <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                    Unless you ask us not to, we may contact you via email in the future to tell you about specials, new products or services, or changes to this privacy policy. You can opt-out of these communications at any time.
                </Typography>

                {/* Section: Analytics Services */}
                <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
                    Analytics Services
                </Typography>
                <Typography variant="body1" paragraph>
                    We may use third-party Service Providers to monitor and analyze the use of our Service.
                </Typography>
                <Typography variant="body1" paragraph>
                    <strong>Google Analytics:</strong> Google Analytics is a web analytics service offered by Google that tracks and reports website traffic. Google uses the data collected to track and monitor the use of our Service. For more information on the privacy practices of Google, please visit the Google Privacy & Terms web page: <Link href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">https://policies.google.com/privacy</Link>
                </Typography>
                <Typography variant="body1" paragraph>
                    You can opt-out of having made your activity on the Service available to Google Analytics by installing the Google Analytics opt-out browser add-on. The add-on prevents the Google Analytics JavaScript (ga.js, analytics.js, and dc.js) from sharing information with Google Analytics about visits activity. You can download it here: <Link href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">https://tools.google.com/dlpage/gaoptout</Link>
                </Typography>


                {/* Section: Your Access and Control */}
                <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
                    Your Access to and Control Over Information
                </Typography>
                <Typography variant="body1" paragraph>
                    You have certain rights regarding the personal information we hold about you. You may opt out of any future marketing contacts from us at any time. Depending on your location, you may have the right to:
                </Typography>
                <List dense sx={{ listStyleType: 'disc', pl: 4 }}>
                    <StyledListItem sx={{ display: 'list-item' }}>
                        <ListItemText primary="See what data we have about you, if any (Access)." />
                    </StyledListItem>
                    <StyledListItem sx={{ display: 'list-item' }}>
                        <ListItemText primary="Change/correct any data we have about you (Correction)." />
                    </StyledListItem>
                    <StyledListItem sx={{ display: 'list-item' }}>
                        <ListItemText primary="Have us delete any data we have about you (Deletion), subject to certain exceptions (e.g., legal requirements, ongoing service needs)." />
                    </StyledListItem>
                    <StyledListItem sx={{ display: 'list-item' }}>
                        <ListItemText primary="Object to or restrict processing of your data." />
                    </StyledListItem>
                    <StyledListItem sx={{ display: 'list-item' }}>
                        <ListItemText primary="Withdraw consent where consent was the basis for processing." />
                    </StyledListItem>
                    <StyledListItem sx={{ display: 'list-item' }}>
                        <ListItemText primary="Express any concern you have about our use of your data." />
                    </StyledListItem>
                </List>
                <Typography variant="body1" paragraph>
                    You can exercise these rights at any time by contacting us via email at <Link href={`mailto:${supportEmail}`}>{supportEmail}</Link>. We may need to verify your identity before processing your request. We will respond to your request within a reasonable timeframe in accordance with applicable law.
                </Typography>

                {/* Subsection: Account Deletion */}
                <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
                    Deleting Your Account and Data
                </Typography>
                <Typography variant="body1" paragraph>
                    You can request the deletion of your account and all associated data directly through the Service. To do so, navigate to "Settings", then "My Account", and select the "Delete Account" option. You will be required to enter your password to confirm this action.
                </Typography>
                <Typography variant="body1" paragraph>
                    Upon successful confirmation, your account and all related Personal Data and Trading Data will be permanently deleted from our active systems, subject to our data retention policies for backups and legal compliance as outlined below. Please note that this action is irreversible.
                </Typography>


                {/* Section: Security */}
                <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
                    Security
                </Typography>
                <Typography variant="body1" paragraph>
                    We take precautions to protect your information. When you submit sensitive information via the Service, your information is protected both online and offline.
                </Typography>
                <Typography variant="body1" paragraph>
                    Wherever we collect sensitive information (such as your Trading Data or Personal Data), that information is encrypted and transmitted to us in a secure way using standard technologies like HTTPS/SSL. We also implement measures such as access controls and secure storage practices to protect data on our servers.
                </Typography>
                <Typography variant="body1" paragraph>
                    However, remember that no method of transmission over the Internet or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your Personal Data, we cannot guarantee its absolute security.
                </Typography>

                {/* Section: Data Retention */}
                <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
                    Data Retention
                </Typography>
                <Typography variant="body1" paragraph>
                    We will retain your Personal Data only for as long as is necessary for the purposes set out in this Privacy Policy. We will retain and use your Personal Data to the extent necessary to comply with our legal obligations (for example, if we are required to retain your data to comply with applicable laws), resolve disputes, and enforce our legal agreements and policies.
                </Typography>
                <Typography variant="body1" paragraph>
                    Usage Data is generally retained for a shorter period, except when this data is used to strengthen the security or to improve the functionality of our Service, or we are legally obligated to retain this data for longer periods.
                </Typography>
                <Typography variant="body1" paragraph>
                    Please refer to our Terms of Service regarding the potential deletion of data associated with inactive or non-subscribed accounts after a certain period.
                </Typography>


                {/* Section: Children's Privacy */}
                <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
                    Children's Privacy
                </Typography>
                <Typography variant="body1" paragraph>
                    Our Service does not address anyone under the age of 18 ("Children"). We do not knowingly collect personally identifiable information from anyone under the age of 18. If you are a parent or guardian and you are aware that your Child has provided us with Personal Data, please contact us. If we become aware that we have collected Personal Data from children without verification of parental consent, we take steps to remove that information from our servers.
                </Typography>

                {/* Section: Links to Other Sites */}
                <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
                    Links to Other Sites
                </Typography>
                <Typography variant="body1" paragraph>
                    Our Service may contain links to other sites that are not operated by us. If you click on a third party link, you will be directed to that third party's site. We strongly advise you to review the Privacy Policy of every site you visit.
                </Typography>
                <Typography variant="body1" paragraph>
                    We have no control over and assume no responsibility for the content, privacy policies, or practices of any third party sites or services.
                </Typography>

                {/* Section: Changes to This Policy */}
                <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
                    Changes to This Privacy Policy
                </Typography>
                <Typography variant="body1" paragraph>
                    We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date at the top.
                </Typography>
                <Typography variant="body1" paragraph>
                    You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page. We may also provide notice via email or through the Service for significant changes.
                </Typography>

                {/* Section: Contact Us */}
                <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
                    Contact Us
                </Typography>
                <Typography variant="body1" paragraph>
                    If you feel that we are not abiding by this privacy policy, or if you have any questions about this Privacy Policy, please contact us:
                </Typography>
                <Typography variant="body1" paragraph>
                    <strong>{companyName}</strong>
                    {companyAddress && <><br />{companyAddress}</>}
                    <br />
                    By email: <Link href={`mailto:${supportEmail}`}>{supportEmail}</Link>
                    <br />
                    By visiting this page on our website: <Link href={websiteUrl} target="_blank" rel="noopener noreferrer">{websiteUrl}</Link>
                </Typography>

            </Container>
        </Box>
    );
}